import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import Button from '../../components/common/Button';
import { Popconfirm, Modal, Divider, message } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, CopyOutlined, DeleteOutlined, PlusOutlined, CheckOutlined, ArrowLeftOutlined, } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Switch from '../editor/components/Switch';
import StyledTable, { TableWrapper } from '../../components/common/StyledTable';
import { useDispatch, useSelector } from 'react-redux';
import { createApiKeyAction, getApiKeysAction, enableApiKeyAction, disableApiKeyAction, deleteApiKeyAction, } from '../../store/apiKeySlice';
import { FONT_FAMILY, PageWrapper, Title, Description, ActionsWrapper, DeleteIconButton, SecretKeyWrapper, ModalLabel, StyledInput, } from './apiKey.styles';
// Masks the secret key by returning a fixed string of asterisks
function maskSecretKey(secretKey) {
    if (!secretKey)
        return '';
    return '*****'; // Always 8 asterisks
}
// Reveals only the first 4 and last 4 characters of the key, masking the middle
function partiallyRevealKey(key) {
    if (!key || key.length <= 8)
        return key;
    return key.slice(0, 4) + '...' + key.slice(-4);
}
// Main component for managing and displaying API keys
const ApiKey = () => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [creating, setCreating] = useState(false);
    const [inputError, setInputError] = useState(null);
    const [data, setData] = useState([]);
    const [unmaskedRows, setUnmaskedRows] = useState({});
    const [copiedRow, setCopiedRow] = useState(null);
    const siteId = useSelector((state) => state?.site?.checkSite?.response?.data?.siteId);
    const apiKeyData = useSelector((state) => state?.apiKey?.apiKey?.response);
    const navigate = useNavigate();
    // Fetches API key data for the current site
    const getApiKeyData = () => {
        try {
            if (siteId) {
                dispatch(getApiKeysAction(siteId));
            }
        }
        catch (err) {
            message.error("Unable to fecth data.Please try again.");
        }
    };
    useEffect(() => {
        getApiKeyData();
    }, []);
    useEffect(() => {
        if (apiKeyData && Array.isArray(apiKeyData)) {
            const mappedData = apiKeyData.map((item) => ({
                apiKeyId: item.apiKeyId,
                name: item.name,
                apiKeyHash: item.apiKeyHash,
                createdDateTime: item.createdDateTime,
                lastUsed: item.lastUsed ?? null,
                createdBy: item.createdBy,
                disabled: item.disabled,
                key: item.apiKeyId,
            }));
            mappedData.sort((a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime());
            setData(mappedData);
        }
    }, [apiKeyData]);
    {
        /*Helper functions*/
    }
    // Opens the modal to create a new API key
    const createApiKey = () => {
        try {
            setModalOpen(true);
            setNewKeyName('');
        }
        catch (error) {
            console.log(error, 'error');
        }
    };
    // Handles deletion of an API key and updates the table
    const handleDelete = (key) => {
        setData((prev) => prev.filter((item) => item.apiKeyId !== key));
        if (siteId) {
            dispatch(deleteApiKeyAction({ siteId, apiKeyId: key })).then(() => {
                getApiKeyData();
            });
        }
    };
    // Handles toggling (enable/disable) of an API key
    const handleToggle = async (key, value) => {
        // Update UI state immediately for responsiveness
        setData((prev) => prev.map((item) => (item.apiKeyId === key ? { ...item, disabled: !value } : item)));
        if (!siteId)
            return;
        const action = value ? enableApiKeyAction : disableApiKeyAction;
        try {
            await dispatch(action({ siteId, apiKeyId: key }));
            getApiKeyData();
        }
        catch (error) {
            message.error('Failed to update API key status');
        }
    };
    // Handles creation of a new API key from the modal
    const handleModalOk = async () => {
        if (!newKeyName.trim()) {
            setInputError('Please enter a name for the API key.');
            return;
        }
        setInputError(null);
        setCreating(true);
        try {
            if (!siteId) {
                throw new Error('Site ID is missing.');
            }
            await dispatch(createApiKeyAction({
                siteId,
                name: newKeyName.trim(),
            })).unwrap();
            getApiKeyData();
            setModalOpen(false);
            setNewKeyName(''); // Reset input
        }
        catch (error) {
            message.error(error?.response?.data?.message || error.message || 'Something went wrong');
        }
        finally {
            setCreating(false);
        }
    };
    // Handles closing the modal and resetting input state
    const handleModalCancel = () => {
        setModalOpen(false);
        setNewKeyName('');
        setInputError(null);
    };
    // Toggles the masked/unmasked state for a specific API key row
    const toggleMask = (apiKeyId) => {
        setUnmaskedRows((prev) => ({ ...prev, [apiKeyId]: !prev[apiKeyId] }));
    };
    // Copies the secret key to the clipboard and shows a message
    const handleCopy = (apiKeyId) => {
        const row = data.find((item) => item.apiKeyId === apiKeyId);
        if (row) {
            navigator.clipboard.writeText(row.apiKeyHash);
            setCopiedRow(apiKeyId);
            message.success('Secret key copied to clipboard');
            setTimeout(() => setCopiedRow(null), 1200);
        }
    };
    // Handles input change for the new API key name and clears error if present
    const handleInputChange = (e) => {
        setNewKeyName(e.target.value);
        if (inputError)
            setInputError(null);
    };
    // Table column definitions for API key table
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (value) => _jsx("span", { style: { fontWeight: 500 }, children: value }),
        },
        {
            title: 'Secret Key',
            dataIndex: 'apiKeyHash',
            key: 'apiKeyHash',
            render: (value, record) => (_jsxs(SecretKeyWrapper, { children: [_jsx("span", { style: { fontFamily: 'monospace', letterSpacing: 1, fontSize: 15 }, children: unmaskedRows[record.apiKeyId] ? partiallyRevealKey(value) : maskSecretKey(value) }), _jsx("button", { "aria-label": unmaskedRows[record.apiKeyId] ? 'Hide secret key' : 'Show secret key', style: {
                            border: 'none',
                            background: 'none',
                            padding: 0,
                            margin: 0,
                            cursor: 'pointer',
                            color: '#888',
                            fontSize: 18,
                            display: 'flex',
                            alignItems: 'center',
                        }, title: unmaskedRows[record.apiKeyId] ? 'Hide' : 'Show', onClick: () => toggleMask(record.apiKeyId), children: unmaskedRows[record.apiKeyId] ? _jsx(EyeInvisibleOutlined, {}) : _jsx(EyeOutlined, {}) }), _jsx("button", { "aria-label": "Copy secret key", style: {
                            border: 'none',
                            background: 'none',
                            padding: 0,
                            margin: 0,
                            cursor: 'pointer',
                            color: copiedRow === record.apiKeyId ? '#52c41a' : '#888',
                            fontSize: 18,
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 4,
                        }, title: "Copy", onClick: () => handleCopy(record.apiKeyId), children: copiedRow === record.apiKeyId ? _jsx(CheckOutlined, {}) : _jsx(CopyOutlined, {}) })] })),
        },
        {
            title: 'Created',
            dataIndex: 'createdDateTime',
            key: 'createdDateTime',
            render: (value) => dayjs(value).format('MMM DD, YYYY'),
        },
        {
            title: 'Last Used',
            dataIndex: 'lastUsed',
            key: 'lastUsed',
            render: (value) => value ? dayjs(value).format('MMM DD, YYYY') : _jsx("span", { style: { color: '#888' }, children: "Never" }),
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (_jsxs(ActionsWrapper, { children: [_jsx(Switch, { value: !record.disabled, onChange: (val) => handleToggle(record.apiKeyId, val ?? false) }), _jsx(Popconfirm, { title: "Are you sure you want to delete this API key?", onConfirm: () => handleDelete(record.apiKeyId), okText: "Delete", okType: "danger", cancelText: "Cancel", placement: "top", children: _jsx(DeleteIconButton, { title: "Delete", children: _jsx(DeleteOutlined, {}) }) })] })),
            width: 120,
            align: 'center',
        },
    ];
    // Table row class for striping and hover
    const rowClassName = (_, index) => (index % 2 === 0 ? 'api-key-row-even' : 'api-key-row-odd');
    return (_jsxs(PageWrapper, { children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', paddingLeft: 32, marginBottom: 8 }, children: [_jsx(Button, { icon: _jsx(ArrowLeftOutlined, {}), onClick: () => navigate('/editor'), style: { marginRight: 16 }, children: "Back to Editor" }), _jsx(Title, { style: { marginBottom: 0 }, children: "Agent Keys" })] }), _jsx(Divider, { style: { margin: '18px 0 0 0', borderColor: '#ececec', width: '100%' } }), _jsxs(Description, { style: { marginTop: 24, marginLeft: 0 }, children: ["As an owner of this organization, you can view and manage all API keys in this organization.", _jsx("br", {}), "Do not share your API key with others or expose it in the browser or other client-side code. To protect your account's security, Becomap may automatically disable any API key that has leaked publicly.", _jsx("br", {})] }), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }, children: _jsxs(Button, { color: "primary", onClick: createApiKey, style: { fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }, children: [_jsx(PlusOutlined, {}), " Create New Agent Key"] }) }), _jsx(TableWrapper, { style: { width: '100%', overflowX: 'auto' }, children: _jsx(StyledTable, { columns: columns, dataSource: data, pagination: false, rowKey: "apiKeyId", rowClassName: rowClassName, scroll: { x: 900 }, locale: {
                        emptyText: (_jsxs("div", { style: { textAlign: 'center', color: '#888', padding: '40px 0' }, children: [_jsx("div", { style: { fontSize: 32, marginBottom: 8 }, children: "\uD83D\uDD11" }), _jsxs("div", { style: { fontSize: 16 }, children: ["No API keys found.", _jsx("br", {}), "Click \"Create New Agent Key\" to add one."] })] })),
                    } }) }), _jsxs(Modal, { title: _jsx("span", { style: { fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 18 }, children: "Create New Secret Key" }), open: modalOpen, onOk: handleModalOk, onCancel: handleModalCancel, okText: "Create", confirmLoading: creating, destroyOnClose: true, styles: { body: { padding: '28px 24px 18px 24px' } }, afterOpenChange: (open) => {
                    if (open) {
                        setTimeout(() => {
                            const input = document.querySelector('input[placeholder="API key name"]');
                            input?.focus();
                        }, 100);
                    }
                }, children: [_jsx(ModalLabel, { children: "Enter a name for your new API key:" }), _jsx("div", { style: { color: '#888', fontSize: 13, marginBottom: 8 }, children: "This name will help you identify this key later." }), _jsx(StyledInput, { value: newKeyName, onChange: handleInputChange, placeholder: "API key name", maxLength: 50, autoFocus: true, required: true, status: inputError ? 'error' : '', "aria-label": "API key name input" }), inputError ? _jsx("div", { style: { color: '#ff4d4f', marginTop: 4, fontSize: 13 }, children: inputError }) : null] }), _jsx("style", { children: `
        .api-key-row-even { background: #fafbfc; }
        .api-key-row-odd { background: #fff; }
        .ant-table-tbody > tr:hover > td { background: #e6f7ff !important; }
      ` })] }));
};
export default ApiKey;
